import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import {
  CheckCircle,
  TrendUp,
  Calendar,
  Lightning,
  Sparkle,
} from "@phosphor-icons/react";

const ISLANDS = ["Oahu", "Maui", "Hawaii Island", "Kauai", "Lanai", "Molokai"];
const UTILITIES = [
  { code: "HECO", label: "HECO — Hawaiian Electric (Oahu)" },
  { code: "MECO", label: "MECO — Maui Electric (Maui)" },
  { code: "HELCO", label: "HELCO — Hawaii Electric Light (Big Island)" },
  { code: "KIUC", label: "KIUC — Kauai Island Utility Coop" },
];

export default function StepReview({ data, onChange, onContinue }) {
  const [form, setForm] = useState({
    utility_provider: data.utility_provider || "HECO",
    monthly_kwh: data.monthly_kwh || "",
    total_amount_due: data.total_amount_due || "",
    rate_per_kwh: data.rate_per_kwh || "",
    island: data.island || "Oahu",
    service_address_street: data.service_address_street || "",
    service_address_city: data.service_address_city || "",
    service_address_zip: data.service_address_zip || "",
  });

  // "What's normal" — prefer avg from history if present, else current bill
  const stats = data.usage_stats;
  const forecast = data.do_nothing_forecast;
  const history = (data.usage_history || []).slice().reverse(); // most recent last

  const [normalKwh, setNormalKwh] = useState(stats?.avg_kwh || data.monthly_kwh || 0);

  useEffect(() => {
    onChange({ ...data, ...form, monthly_kwh: parseFloat(normalKwh) || form.monthly_kwh });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, normalKwh]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = (parseFloat(normalKwh) > 0 || form.monthly_kwh > 0) && form.utility_provider && form.island;

  return (
    <div data-testid="step-review">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5 mb-5">
            <CheckCircle size={14} weight="fill" className="text-emerald-600" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
              AI extracted your bill
            </span>
          </div>
          <h2 className="font-display text-4xl font-bold text-slate-950 tracking-tight">
            Here's what your bill is telling us
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            Hawaii utility bills include 12-13 months of usage history. We pulled all of it — confirm
            the details below and tell us what a "normal" month looks like for you.
          </p>
        </div>

        {/* Big-money callout + side year ladder */}
        {forecast && (
          <div className="grid lg:grid-cols-12 gap-5 mb-8" data-testid="review-cost-callout">
            <div className="lg:col-span-7 rounded-3xl bg-gradient-to-br from-slate-950 to-slate-800 p-8 md:p-10 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-red-500/15 rounded-full blur-3xl" />
              <div className="relative">
                <div className="text-xs uppercase tracking-[0.2em] text-red-300 font-bold mb-2">
                  Your annual cost of doing nothing
                </div>
                <div className="font-display text-6xl md:text-7xl font-bold text-white">
                  ${stats?.annual_cost ? stats.annual_cost.toLocaleString(undefined, {maximumFractionDigits:0}) : "—"}
                </div>
                <p className="text-slate-300 mt-3 text-sm">
                  Based on {stats?.avg_kwh ? Math.round(stats.avg_kwh) : "—"} kWh average ×{" "}
                  {data.utility_provider || "HECO"} rate. Hawaii rates climb ~4.5% per year.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5 rounded-3xl bg-white border border-slate-200 p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-3">
                What it costs to wait
              </div>
              <div className="space-y-2">
                {[3, 5, 7, 9, 12].map((y) => (
                  <YearMarker
                    key={y}
                    year={y}
                    cumulative={forecast.markers[y]}
                  />
                ))}
              </div>
              <div className="mt-4 text-[11px] text-slate-500 leading-relaxed">
                Cumulative grid payments at HECO's historical 4.5% annual rate increase.
              </div>
            </div>
          </div>
        )}

        {/* 13-month usage history chart + stats */}
        {stats && history.length > 0 && (
          <div className="card-clean p-8 mb-8" data-testid="review-history">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
              <h3 className="font-display text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Calendar size={20} weight="duotone" className="text-sky-500" />
                Your last {stats.months_count} months
              </h3>
              <div className="flex items-center gap-3 text-xs">
                <Pill color="emerald" label="Low" value={`${Math.round(stats.low_kwh)} kWh`} />
                <Pill color="sky" label="Avg" value={`${Math.round(stats.avg_kwh)} kWh`} />
                <Pill color="red" label="High" value={`${Math.round(stats.high_kwh)} kWh`} />
              </div>
            </div>
            <p className="text-sm text-slate-500 mb-5">
              Bars over your average are usually summer months when AC works hardest.
            </p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={history} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <XAxis dataKey="period" stroke="#94A3B8" tick={{ fontSize: 11 }} />
                <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }}
                  formatter={(v) => `${Math.round(v)} kWh`}
                />
                <ReferenceLine
                  y={stats.avg_kwh}
                  stroke="#0EA5E9"
                  strokeDasharray="4 4"
                  label={{ value: "Avg", fontSize: 11, fill: "#0EA5E9", position: "right", fontWeight: 700 }}
                />
                <Bar dataKey="kwh" radius={[8, 8, 0, 0]}>
                  {history.map((h, i) => {
                    const k = h.kwh || 0;
                    const color = k >= stats.high_kwh * 0.95 ? "#EF4444" : k <= stats.low_kwh * 1.05 ? "#10B981" : "#0EA5E9";
                    return <Cell key={i} fill={color} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* What's normal picker */}
        {stats && (
          <div className="card-clean p-8 mb-8" data-testid="review-normal-picker">
            <h3 className="font-display text-xl font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-amber-500" /> What's a "normal" month for you?
            </h3>
            <p className="text-sm text-slate-500 mb-5">
              We use this number to size your savings plan. Pick whichever one fits your reality best.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <NormalChoice
                label="Low month (mild weather)"
                value={Math.round(stats.low_kwh)}
                selected={Math.round(normalKwh) === Math.round(stats.low_kwh)}
                onClick={() => setNormalKwh(stats.low_kwh)}
                tone="emerald"
              />
              <NormalChoice
                label="Average (recommended)"
                value={Math.round(stats.avg_kwh)}
                selected={Math.round(normalKwh) === Math.round(stats.avg_kwh)}
                onClick={() => setNormalKwh(stats.avg_kwh)}
                tone="sky"
                recommended
              />
              <NormalChoice
                label="High month (peak summer)"
                value={Math.round(stats.high_kwh)}
                selected={Math.round(normalKwh) === Math.round(stats.high_kwh)}
                onClick={() => setNormalKwh(stats.high_kwh)}
                tone="red"
              />
            </div>
            <div className="mt-5 flex items-center gap-3">
              <span className="text-sm font-semibold text-slate-700">Or enter custom:</span>
              <input
                type="number"
                value={normalKwh}
                onChange={(e) => setNormalKwh(parseFloat(e.target.value) || 0)}
                className="input-clean w-32"
                data-testid="review-normal-custom-input"
              />
              <span className="text-sm text-slate-500">kWh / month</span>
            </div>
          </div>
        )}

        {/* Confirm details form */}
        <div className="card-clean p-8 space-y-5">
          <h3 className="font-display text-lg font-semibold text-slate-900">Confirm bill details</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Utility Provider">
              <select value={form.utility_provider} onChange={(e) => update("utility_provider", e.target.value)} className="input-clean" data-testid="review-utility-select">
                {UTILITIES.map((u) => <option key={u.code} value={u.code}>{u.label}</option>)}
              </select>
            </Field>
            <Field label="Island">
              <select value={form.island} onChange={(e) => update("island", e.target.value)} className="input-clean" data-testid="review-island-select">
                {ISLANDS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            <Field label="Monthly kWh (current bill)">
              <input type="number" value={form.monthly_kwh} onChange={(e) => update("monthly_kwh", parseFloat(e.target.value) || 0)} className="input-clean" data-testid="review-kwh-input" />
            </Field>
            <Field label="Total Bill ($)">
              <input type="number" step="0.01" value={form.total_amount_due} onChange={(e) => update("total_amount_due", parseFloat(e.target.value) || 0)} className="input-clean" data-testid="review-amount-input" />
            </Field>
            <Field label="Rate ($/kWh)">
              <input type="number" step="0.0001" value={form.rate_per_kwh} onChange={(e) => update("rate_per_kwh", parseFloat(e.target.value) || 0)} className="input-clean" data-testid="review-rate-input" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            <Field label="Street">
              <input type="text" value={form.service_address_street} onChange={(e) => update("service_address_street", e.target.value)} className="input-clean" data-testid="review-street-input" />
            </Field>
            <Field label="City">
              <input type="text" value={form.service_address_city} onChange={(e) => update("service_address_city", e.target.value)} className="input-clean" data-testid="review-city-input" />
            </Field>
            <Field label="Zip">
              <input type="text" value={form.service_address_zip} onChange={(e) => update("service_address_zip", e.target.value)} className="input-clean" data-testid="review-zip-input" />
            </Field>
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <button data-testid="review-continue-btn" onClick={onContinue} disabled={!valid} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
            Looks good — continue
          </button>
        </div>
      </div>
    </div>
  );
}

function YearMarker({ year, cumulative }) {
  if (!cumulative) return null;
  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: year * 0.04 }}
      className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
      data-testid={`year-marker-${year}`}
    >
      <span className="text-sm text-slate-700">In <span className="font-bold text-slate-900">{year} years</span></span>
      <span className="font-mono font-bold text-slate-950">
        ${(cumulative / 1000).toFixed(1)}k
      </span>
    </motion.div>
  );
}

function Pill({ color, label, value }) {
  const colorMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <div className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${colorMap[color]}`}>
      {label}: <span className="font-mono">{value}</span>
    </div>
  );
}

function NormalChoice({ label, value, selected, onClick, tone, recommended }) {
  const toneMap = {
    emerald: { ring: "border-emerald-500 ring-emerald-500/15", text: "text-emerald-600" },
    sky: { ring: "border-sky-500 ring-sky-500/15", text: "text-sky-600" },
    red: { ring: "border-red-500 ring-red-500/15", text: "text-red-600" },
  };
  const t = toneMap[tone] || toneMap.sky;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`normal-${tone}`}
      className={`text-left p-5 rounded-2xl border-2 transition-all ${selected ? `${t.ring} ring-4` : "border-slate-200 hover:border-slate-300"} bg-white`}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-slate-700">{label}</span>
        {recommended && <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Pick this</span>}
      </div>
      <div className={`font-display text-3xl font-bold mt-2 ${t.text}`}>{value}<span className="text-sm font-medium text-slate-500"> kWh</span></div>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-slate-700 mb-2">{label}</span>
      {children}
    </label>
  );
}
